import Papa from 'papaparse';
import moment from 'moment';
import type { CurvePoint, CurveSchedule } from './types';

// ─────────────────────────────────────
// CSV parsing — custom-profile curve import
// ─────────────────────────────────────

/** Output of parseCsv when the file is parseable and has at least one date and numeric column. */
export interface ParsedCsv {
  source: string;
  dateColumn: string;
  numericColumns: string[];
  /** Per-column ordered list of { date, value } where date is normalized to YYYY-MM-DD first-of-month. */
  rowsByColumn: Record<string, Array<{ date: string; value: number }>>;
}

const MONTH_NAMES: Record<string, number> = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};

const MONTH_FULL_NAMES = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
];

const pad2 = (n: number): string => String(n).padStart(2, '0');

/**
 * Normalizes a raw date-cell string to `YYYY-MM-DD` first-of-month, matching the
 * backend's strict month-date contract. Returns null when the string doesn't
 * match any supported format. Supported inputs:
 *   - `YYYY-MM` / `YYYY-MM-DD`
 *   - `MMM-YY` / `MMM-YYYY` (case-insensitive month abbreviation; dash/space/slash separator)
 *   - `M/YYYY` / `MM/YYYY`
 *   - `MM/YY` (2-digit year: >=50 → 19xx, <50 → 20xx — matches the mock's convention)
 * @param {string} raw - The cell value as read from the CSV
 * @return {string | null} `YYYY-MM-DD` (first of month) or null
 */
export function coerceMonthDate(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;

  // YYYY-MM-DD (force first of month) or YYYY-MM
  let m = trimmed.match(/^(\d{4})-(\d{1,2})(?:-(\d{1,2}))?$/);
  if (m) {
    const year = Number(m[1]);
    const month = Number(m[2]);
    if (month < 1 || month > 12) return null;
    return `${year}-${pad2(month)}-01`;
  }

  // MMM-YY or MMM-YYYY: require a month abbreviation (3 letters) optionally
  // followed by the rest of the month name; separator -, /, or space.
  // Rejects arbitrary letter prefixes like "Marathon-2026" by checking the
  // remainder against the canonical month names.
  m = trimmed.match(/^([A-Za-z]+)[-/ ](\d{2}|\d{4})$/);
  if (m) {
    const lower = m[1].toLowerCase();
    const month = MONTH_NAMES[lower.slice(0, 3)];
    if (!month) return null;
    if (lower.length > 3 && !MONTH_FULL_NAMES.includes(lower)) return null;
    let year = Number(m[2]);
    if (m[2].length === 2) year += year >= 50 ? 1900 : 2000;
    return `${year}-${pad2(month)}-01`;
  }

  // M/YYYY or MM/YYYY
  m = trimmed.match(/^(\d{1,2})\/(\d{4})$/);
  if (m) {
    const month = Number(m[1]);
    const year = Number(m[2]);
    if (month < 1 || month > 12) return null;
    return `${year}-${pad2(month)}-01`;
  }

  // MM/YY (2-digit year)
  m = trimmed.match(/^(\d{1,2})\/(\d{2})$/);
  if (m) {
    const month = Number(m[1]);
    if (month < 1 || month > 12) return null;
    const yy = Number(m[2]);
    const year = yy >= 50 ? 1900 + yy : 2000 + yy;
    return `${year}-${pad2(month)}-01`;
  }

  return null;
}

const COLUMN_DETECT_THRESHOLD = 0.8;

/**
 * Tokenizes a CSV string and detects which column is the date and which are
 * numeric. Single-pass: walks every cell once, caching both the date- and
 * numeric-coercions per field, then classifies the columns and builds the
 * per-numeric-column `{ date, value }` lists from the cache. Returns
 * `{ error }` when the file is not a usable curve source (no rows, no date
 * column, or no numeric columns).
 * @param {string} text - Raw CSV text
 * @param {string} filename - For error messages
 * @return {ParsedCsv | { error: string }}
 */
export function parseCsv(
  text: string,
  filename: string
): ParsedCsv | { error: string } {
  const result = Papa.parse<Record<string, string>>(text, {
    header: true,
    skipEmptyLines: 'greedy',
    dynamicTyping: false,
  });

  const fields = result.meta.fields ?? [];
  const rows = result.data ?? [];
  const parseError = {
    error: `Could not parse "${filename}". Expected a CSV with a date column and at least one numeric column.`,
  };

  if (fields.length === 0 || rows.length === 0) return parseError;

  // One pass over every cell. Per field, accumulate hit counts for date- and
  // numeric-coercion and cache the coerced values so we can build rowsByColumn
  // without re-coercing.
  interface FieldStats {
    dateHits: number;
    numHits: number;
    total: number;
    dates: Array<string | null>;
    nums: Array<number | null>;
  }
  const statsByField: Record<string, FieldStats> = {};
  for (const field of fields) {
    statsByField[field] = {
      dateHits: 0,
      numHits: 0,
      total: 0,
      dates: new Array<string | null>(rows.length).fill(null),
      nums: new Array<number | null>(rows.length).fill(null),
    };
  }

  for (let rowIdx = 0; rowIdx < rows.length; rowIdx += 1) {
    const row = rows[rowIdx];
    for (const field of fields) {
      const cell = row[field];
      if (cell === null || cell === undefined) continue;
      const trimmed = String(cell).trim();
      if (trimmed === '') continue;
      const stats = statsByField[field];
      stats.total += 1;
      const date = coerceMonthDate(trimmed);
      if (date !== null) {
        stats.dateHits += 1;
        stats.dates[rowIdx] = date;
      }
      const num = Number(trimmed);
      if (Number.isFinite(num)) {
        stats.numHits += 1;
        stats.nums[rowIdx] = num;
      }
    }
  }

  // Date column: first field with date-hit rate >= threshold.
  const dateColumn = fields.find((f) => {
    const s = statsByField[f];
    return s.total > 0 && s.dateHits / s.total >= COLUMN_DETECT_THRESHOLD;
  });
  if (!dateColumn) return parseError;

  // Numeric columns: any non-date field with num-hit rate >= threshold.
  const numericColumns = fields.filter((f) => {
    if (f === dateColumn) return false;
    const s = statsByField[f];
    return s.total > 0 && s.numHits / s.total >= COLUMN_DETECT_THRESHOLD;
  });
  if (numericColumns.length === 0) return parseError;

  // Build rowsByColumn from the per-row cache — no re-coercion. Rows whose
  // date or value couldn't coerce earlier are skipped here.
  const dateCache = statsByField[dateColumn].dates;
  const rowsByColumn: Record<
    string,
    Array<{ date: string; value: number }>
  > = {};
  for (const col of numericColumns) {
    const numCache = statsByField[col].nums;
    const out: Array<{ date: string; value: number }> = [];
    for (let i = 0; i < rows.length; i += 1) {
      const date = dateCache[i];
      const value = numCache[i];
      if (date !== null && value !== null) out.push({ date, value });
    }
    rowsByColumn[col] = out;
  }

  return { source: filename, dateColumn, numericColumns, rowsByColumn };
}

/**
 * Final validation pass for a chosen value column: enforces the BE's contract
 * (dates unique and strictly ascending in source order, values finite). Does
 * NOT reorder the rows — a CSV that arrives out of order is rejected with the
 * chronological-order error rather than being silently sorted. Matches the
 * plan's verification step 15b.
 * @param {ParsedCsv} parsed - The result of parseCsv
 * @param {string} valueColumn - One of parsed.numericColumns
 * @return {{ points: CurvePoint[] } | { error: string }}
 */
export function extractCurvePoints(
  parsed: ParsedCsv,
  valueColumn: string
): { points: CurvePoint[] } | { error: string } {
  const points = parsed.rowsByColumn[valueColumn] ?? [];
  if (points.length === 0) {
    return { error: `No usable rows found in column "${valueColumn}".` };
  }

  for (let i = 1; i < points.length; i += 1) {
    if (points[i].date <= points[i - 1].date) {
      return { error: 'Dates must be unique and in chronological order.' };
    }
  }

  for (const point of points) {
    if (!Number.isFinite(point.value)) {
      return { error: 'One or more values are not numeric.' };
    }
  }

  return { points };
}

// ─────────────────────────────────────
// Curve tail projection — used by the sparkline's dashed continuation
// ─────────────────────────────────────

/**
 * Advances a `YYYY-MM-DD` first-of-month date by N months and returns the new
 * date in the same `YYYY-MM-DD` first-of-month format.
 * @param {string} yyyymmdd - Starting date in `YYYY-MM-DD` (first-of-month) form
 * @param {number} monthsAhead - Number of months to add (can be 0)
 * @return {string} The advanced date in `YYYY-MM-DD` (first-of-month) form
 */
function advanceMonths(yyyymmdd: string, monthsAhead: number): string {
  return moment(yyyymmdd).add(monthsAhead, 'months').format('YYYY-MM-DD');
}

/**
 * Projects the curve's tail forward `monthsAhead` months starting from the
 * month AFTER the last imported point. Used to draw the dashed continuation
 * on the inline sparkline. Behavior per tail kind:
 *   - 'hold_flat'  — repeat last value for each month
 *   - 'escalating' — apply pct compounding every periodCount × period months
 *   - 'stepped'    — emit one point per month walking the segments (terminal
 *                    open-ended segment fills the remaining horizon)
 * @param {CurveSchedule} curve - The curve to project from
 * @param {number} monthsAhead - How many months of projection to emit
 * @return {CurvePoint[]} Projected continuation; empty if the curve has no points
 */
export function projectCurveTail(
  curve: CurveSchedule,
  monthsAhead: number
): CurvePoint[] {
  if (curve.points.length === 0 || monthsAhead <= 0) return [];

  const lastPoint = curve.points[curve.points.length - 1];
  const tail = curve.tail;

  if (tail.kind === 'hold_flat') {
    const out: CurvePoint[] = [];
    for (let i = 1; i <= monthsAhead; i += 1) {
      out.push({
        date: advanceMonths(lastPoint.date, i),
        value: lastPoint.value,
      });
    }
    return out;
  }

  if (tail.kind === 'escalating') {
    const esc = tail.escalation;
    if (!esc) return [];
    const periodMonths =
      esc.period === 'annually'
        ? 12 * (Number(esc.periodCount) || 1)
        : Number(esc.periodCount) || 1;
    const pct = (Number(esc.pctPerPeriod) || 0) / 100;
    const sign = esc.direction === 'descending' ? -1 : 1;
    const factorPerStep = 1 + sign * pct;

    const out: CurvePoint[] = [];
    let value = lastPoint.value;
    for (let i = 1; i <= monthsAhead; i += 1) {
      // Compound once per `periodMonths`.
      if (periodMonths > 0 && i % periodMonths === 0) {
        value = value * factorPerStep;
      }
      out.push({ date: advanceMonths(lastPoint.date, i), value });
    }
    return out;
  }

  if (tail.kind === 'stepped') {
    const segments = tail.segments ?? [];
    if (segments.length === 0) return [];

    const out: CurvePoint[] = [];
    let emitted = 0;
    for (const seg of segments) {
      const value = Number(seg.value) || 0;
      const duration =
        seg.durationMonths === null || seg.durationMonths === ''
          ? monthsAhead - emitted // terminal: fill the remaining horizon
          : Math.max(0, Number(seg.durationMonths));
      for (let i = 0; i < duration && emitted < monthsAhead; i += 1) {
        emitted += 1;
        out.push({ date: advanceMonths(lastPoint.date, emitted), value });
      }
      if (emitted >= monthsAhead) break;
    }
    return out;
  }

  return [];
}
